import React, { useEffect, useState } from 'react';

import { useSelector } from 'react-redux';
import { Link } from '~/lib/react-router-dom';
import { RootState } from '~/reducers';

import { Button } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import { fetchPaginatedPromotions } from '~/actions/promotions';

import { useAppDispatch } from '~/hooks/store';

import { formatDateShortDD, formatDateWithClock } from '~/services/TimeService';

import GridPagination from './Elements/GridPagination';

interface Props {
  source: string;
}

const PAGE_SIZE = 20;

export default function PaginatedPromotionList({ source }: Props) {
  const dispatch = useAppDispatch();
  const { total, loading, promotions } = useSelector((state: RootState) => state.promotions);
  const [page, setPage] = useState(0);

  useEffect(() => {
    dispatch(fetchPaginatedPromotions(source, page + 1, PAGE_SIZE));
  }, [dispatch, source, page]);

  const viewRatePlantBtnFormatter = (vendorId: string, ratePlanId: string) => {
    return (
      <Button component={Link} to={`vendors/${vendorId}/rate-plans/${ratePlanId}`} variant="text">
        View
      </Button>
    );
  };

  const formatSchedulePeriod = (
    schedules: Array<{
      activePeriod: { from: string; to: string };
      travelPeriod: { from: string; to: string };
      type: string;
      parent: string;
    }>,
  ) => {
    if (!schedules || schedules.length === 0) {
      return 'No schedules';
    }
    const firstSchedule = schedules[0];
    const activePeriod = firstSchedule.activePeriod;
    return `${formatDateShortDD(activePeriod.from)} - ${formatDateShortDD(activePeriod.to)}`;
  };

  const formatLengthOfStay = (minLos: number | null, maxLos: number | null) => {
    if (minLos && maxLos) {
      return `${minLos} - ${maxLos} nights`;
    }
    if (minLos) {
      return `${minLos}+ nights`;
    }
    if (maxLos) {
      return `Up to ${maxLos} nights`;
    }
    return 'Any length';
  };

  const columns: Array<GridColDef> = [
    {
      field: 'discount_name',
      headerName: 'Promotion Name',
      disableColumnMenu: true,
      minWidth: 150,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return params.value || 'Unnamed Promotion';
      },
    },
    {
      field: 'discount_type',
      headerName: 'Type',
      disableColumnMenu: true,
      minWidth: 120,
      flex: 0.8,
      renderCell: (params: GridRenderCellParams) => {
        return params.value;
      },
    },
    {
      field: 'discount_percent',
      headerName: 'Discount %',
      disableColumnMenu: true,
      minWidth: 100,
      flex: 0.6,
      renderCell: (params: GridRenderCellParams) => {
        return params.value;
      },
    },
    {
      field: 'length_of_stay',
      headerName: 'Length of Stay',
      disableColumnMenu: true,
      minWidth: 130,
      flex: 0.8,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        return formatLengthOfStay(params.row.min_los, params.row.max_los);
      },
    },
    {
      field: 'paid_by',
      headerName: 'Paid By',
      disableColumnMenu: true,
      minWidth: 100,
      flex: 0.6,
      renderCell: (params: GridRenderCellParams) => {
        return params.value;
      },
    },
    {
      field: 'is_displayed',
      headerName: 'Is displayed',
      disableColumnMenu: true,
      minWidth: 100,
      flex: 0.6,
      renderCell: (params: GridRenderCellParams) => {
        return params.value ? 'Yes' : 'No';
      },
    },
    {
      field: 'schedules',
      headerName: 'Active Period',
      disableColumnMenu: true,
      minWidth: 180,
      flex: 1,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        return formatSchedulePeriod(params.value);
      },
    },
    {
      field: 'vendor_name',
      headerName: 'Vendor Name',
      disableColumnMenu: true,
      minWidth: 150,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return params.value || 'N/A';
      },
    },
    {
      field: 'created_at',
      headerName: 'Created At',
      disableColumnMenu: true,
      minWidth: 150,
      flex: 0.8,
      renderCell: (params: GridRenderCellParams) => {
        return params.value ? formatDateWithClock(params.value) : 'N/A';
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      disableColumnMenu: true,
      sortable: false,
      minWidth: 70,
      flex: 0,
      renderCell: (params: GridRenderCellParams) => {
        return viewRatePlantBtnFormatter(params.row.id_salesforce_external, params.row.rate_plan_id);
      },
    },
  ];

  return (
    <DataGrid
      className="T-promotion-table"
      loading={loading}
      rows={promotions || []}
      columns={columns}
      pageSizeOptions={[PAGE_SIZE]}
      paginationModel={{ pageSize: PAGE_SIZE, page }}
      onPaginationModelChange={({ page }) => setPage(page)}
      getRowId={(row) => row.id}
      getRowHeight={() => 'auto'}
      rowCount={total}
      paginationMode="server"
      slots={{
        pagination: GridPagination,
      }}
      autoHeight
      disableRowSelectionOnClick
      sx={{ mt: 2 }}
    />
  );
}
